<template>
  <BaseLayout :title="`${subtask.homework_title}: ${subtask.title}`">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div class="card">
          <h5 class="card-header">{{ subtask.title }}</h5>
          <div class="card-body">
            <div class="card">
              <h5 class="card-header">Description</h5>
              <!-- Render parsed markdown as HTML -->
              <div class="card-body" v-html="formattedDescription"></div>
            </div>
          </div>
          <!-- Display only when the task is for evaluation -->
          <div v-if="subtask.to_evaluate" class="card-body">
            <div class="row">
              <div class="col-md-6">
                <h5 class="card-title">
                  Submission Files  
                  <i class="bi bi-question-circle" v-tooltip="'Submission files are files that you submit for the task. The submission files must be named exactly same.'"></i>
                </h5>
                <ul class="list-group">
                  <li v-for="file in subtask.submission_file_paths" :key="file" class="list-group-item">
                    {{ getFileName(file) }}
                  </li>
                  <li v-if="subtask.submission_file_paths.length === 0" class="list-group-item alert alert-info">No submission files. Contact teacher, please.</li>
                </ul>
              </div>
              <div v-if="isTeacher || (!isTeacher && subtask.resource_file_paths > 0)" class="col-md-6">
                <h5 class="card-title">
                  Resource Files  
                  <i class="bi bi-question-circle" v-tooltip="'Resource files are necessary for completing the task, but they are not submitted. Not all tasks have resource files.'"></i>
                </h5>
                <ul class="list-group">
                  <li v-for="file in subtask.resource_file_paths" :key="file" class="list-group-item">
                    {{ getFileName(file) }}
                  </li>
                  <li v-if="subtask.resource_file_paths.length === 0" class="list-group-item alert alert-info">No resource files</li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="subtask.to_evaluate && !isTeacher" class="card-body">
            <div class="list-group-item">
              <div class="card-title mb-0 d-flex align-items-center">
                <h5>
                  Submissions
                  <i class="bi bi-question-circle ms-2" v-tooltip="'Latest submission will be evaluated. Of course, only submitted before deadline.'"></i>
                </h5>
                <span class="ms-3 d-flex align-items-center">
                  <span><small>Sanity check enabled:</small></span>
                  <i v-if="(subtask.test_pre_check_file_paths && subtask.test_pre_check_file_paths.length > 0)" class="bi bi-toggle-on text-success fs-2 ms-1"
                    v-tooltip="'If you see `Sanity Check: Not available yet`, it means the sanity check is still running. If Sanity Check was activated later, it won\'t apply to previously sent files.'"
                  ></i>
                  <i v-else class="bi bi-toggle-off text-danger fs-2 ms-1"
                    v-tooltip="'You will only see `Sanity Check: Not available yet`, and this status will not change, as the Sanity Check will not run.'"
                  ></i>
                </span>
              </div>
              <p class="alert alert-warning"><b>Note:</b> Sanity check is only an informational review of submitted files and not an actual evaluation.</p>
            </div>
            <div v-if="!(subtask.student_submissions && subtask.student_submissions.length)" class="alert alert-info">
              No submissions yet.
            </div>
            <ul v-else class="list-group">
              <li v-for="submission in sortedSubmissions" :key="submission.id" class="list-group-item d-flex justify-content-between align-items-center">
                <span>
                  {{ formatedSubmissionDate(submission.submission_date) }} - Sanity check: 
                  <span v-if="submission.precheck_successfull === true">
                    <i class="bi bi-check-circle-fill text-success" v-tooltip="'Sanity check passed. This is not evaluation of homework\'s task.'"></i>
                  </span>
                  <span v-else-if="submission.precheck_successfull === false">
                    <i class="bi bi-x-circle-fill text-danger" v-tooltip="'Sanity check failed'"></i>
                  </span>
                  <span v-else>
                    Not available yet
                  </span>
                </span>
                <span>
                  <button v-if="submission.precheck_log_file_path" class="btn btn-warning btn-sm me-2" @click="downloadFile(submission.precheck_log_file_path)">
                    <i class="bi bi-file-earmark-text"></i> Download sanity check
                  </button>
                  <!-- Display to impersonate teacher, because submissions are shown only to students -->
                  <button v-if="submission.container_log_path && (isTeacher || isImpersonating)" class="btn btn-info btn-sm me-2" @click="downloadFile(submission.container_log_path)">
                    <i class="bi bi-file-earmark-text"></i> Download container log
                  </button>
                  <button class="btn btn-primary btn-sm" @click="downloadFiles(submission.file_paths, 'submission_files.zip')">
                    <i class="bi bi-download"></i> Download submission
                  </button>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Teacher-only: AI-authored bug instructions for this subtask -->
        <BugInstructionList v-if="isTeacher && subtask.id" :subtaskId="subtask.id" />
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="subtask.to_evaluate && !isTeacher && !isImpersonating" class="card p-3 mb-3">
        <h5 class="card-title">Submission:</h5>
        <button class="btn btn-success" @click="submitFilesClick" data-bs-toggle="modal" data-bs-target="#submitFilesModal">
          <i class="bi bi-upload"></i> Submit files
        </button>
      </div>
      <div class="card p-3">
        <h5 class="card-title">Actions:</h5>
        <router-link :to="`/homework/${subtask.homework_id}`" class="btn btn-primary mb-2">
            <i class="bi bi-arrow-left"></i> Back to Homework
        </router-link>
        <router-link v-if="isTeacher" :to="`/subtask/edit/${subtask.id}`" class="btn btn-secondary mb-2">
          <i class="bi bi-pencil"></i> Edit Task
        </router-link>
        <button v-if="isTeacher" @click="deleteSubtask(subtask.id)" class="btn btn-danger mb-2">
          <i class="bi bi-trash"></i> Delete Task
        </button>
        <button v-if="isTeacher" class="btn btn-info" @click="downloadFiles(subtask.submission_file_paths, 'subtask_files.zip')">
          <i class="bi bi-download"></i> Download task files
        </button>
      </div>
    </template>

    <!-- Submit Files Modal -->
    <SubmitFilesModal
      v-if="!isTeacher"
      ref="submitFilesModal"
      :required_file_paths="subtask.submission_file_paths"
      @submitForm="uploadSubmissionFiles"
    />

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import SubmitFilesModal from '@/components/SubmitFilesModal.vue';
import BugInstructionList from '@/components/BugInstructionList.vue';
import SubtaskController from '@/controllers/SubtaskController';
import SubmissionController from '@/controllers/SubmissionController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import Submission from '@/models/submission';
import { marked } from 'marked';
import { mapGetters } from 'vuex';

export default {
  name: 'SubtaskDetailView',
  components: {
    BaseLayout,
    SubmitFilesModal,
    BugInstructionList,
    StemErrorModal,
  },
  data() {
    return {
      subtask: {},
      loading: true,
      isDownloading: false,
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId', 'isImpersonating']),
    formattedDescription() {

      return marked.parse(this.subtask.description || '');
    },
    formatedSubmissionDate() {
      return (date) => Submission.formatDate(date);
    },
    sortedSubmissions() {
      return (this.subtask.student_submissions || [])
               .slice()
               .sort((a, b) => new Date(b.submission_date) - new Date(a.submission_date));
    },
  },
  async created() {
    try {
      const subtaskId = this.$route.params.id;
      if (this.isTeacher) {
        this.subtask = await SubtaskController.fetchSubtaskById(subtaskId);
      } else {
        this.subtask = await SubtaskController.fetchSubtaskByIdWithStudentsSubmissions(subtaskId);
      }
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
    async deleteSubtask(subtaskId) {
      if (confirm('Are you sure you want to delete this task?')) {
        try {
          await SubtaskController.deleteSubtask(subtaskId);
          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Task deleted successfully',
          });
          this.$router.push(`/homework/${this.subtask.homework_id}`);
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async uploadSubmissionFiles(filesToSubmit) {
      try {
        if (filesToSubmit.length > 0) {
          const formData = new FormData();
          for (const file of filesToSubmit) {
            formData.append('files', file);
          }

          await SubmissionController.createSubmission(this.subtask.id, formData);
          const subtaskId = this.$route.params.id;
          this.subtask = await SubtaskController.fetchSubtaskByIdWithStudentsSubmissions(subtaskId);
          
          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Files submitted successfully',
          });
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    // Download single file
    async downloadFile(filePath) {
      try {
        const response = await SubtaskController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    // Download multiple files
    async downloadFiles(filePaths, zipName) {
      try {
        const response = await SubtaskController.downloadFiles(filePaths);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', zipName || 'files.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    submitFilesClick() {
      this.$store.dispatch('refreshToken');
    },
  },
};
</script>